import type { TableSourceItem } from "./sourceItems";
import type { FactSetCitationFqlMap } from "../types";

export function splitMarkdownTableRow(row: string): string[] {
  let normalized = row.trim();
  if (normalized.startsWith("|")) normalized = normalized.slice(1);
  if (normalized.endsWith("|")) normalized = normalized.slice(0, -1);
  return normalized.split("|").map((cell) => cell.trim());
}

export function isMarkdownSeparatorRow(row: string | undefined): boolean {
  return Boolean(row && /^\|[\s:|-]+\|$/.test(row.trim()));
}

export function stripInlineMarkup(value: string): string {
  return value
    .replace(/<u\b[^>]*>(.*?)<\/u>/gi, "$1")
    .replace(/<[^>]+>/g, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/`(.*?)`/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export function getTableCompanyEntities(markdown: string, citedUuids: string[]): string[] {
  const tableRows = markdown
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.startsWith("|") && !isMarkdownSeparatorRow(line))
    .map((line) => splitMarkdownTableRow(line));
  if (tableRows.length < 2) return [];

  const [headers, ...rows] = tableRows;
  return (
    getTransposedCompanyEntities(rows) ??
    getRowOrientedCompanyEntities(headers, rows, citedUuids) ??
    []
  );
}

export function getFqlTickers(item: TableSourceItem): string[] {
  const fql = item.envelope.FQL ?? item.envelope.fql;
  return getFqlTickersFromMap(fql, item.citedUuids);
}

export function getFirstFqlTicker(item: TableSourceItem): string | undefined {
  return getFirstFqlTickerFromMap(item.envelope.FQL ?? item.envelope.fql, item.citedUuids);
}

export function getFirstFqlTickerFromMap(
  fql: unknown,
  preferredUuids: string[]
): string | undefined {
  const [first] = getFqlTickersFromMap(fql, preferredUuids, { includeAllFallback: true });
  return first;
}

function getFqlTickersFromMap(
  fql: unknown,
  preferredUuids: string[],
  options: { includeAllFallback?: boolean } = {}
): string[] {
  if (!fql || typeof fql !== "object") return [];
  const map = fql as FactSetCitationFqlMap;
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (ticker: string | null | undefined): void => {
    const trimmed = typeof ticker === "string" ? ticker.trim() : "";
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    out.push(trimmed);
  };

  for (const id of preferredUuids) add(map[id]?.ticker);
  if (out.length > 0 || !options.includeAllFallback) return out;

  const ids = Object.keys(fql);
  for (const id of ids) add(map[id]?.ticker);
  return out;
}

function getTransposedCompanyEntities(rows: string[][]): string[] | undefined {
  for (const row of rows) {
    if (!isCompanyDescriptor(row[0] ?? "")) continue;

    const entities = collectCompanyEntities(row.slice(1));
    if (entities.length > 0) return entities;
  }
  return undefined;
}

function getRowOrientedCompanyEntities(
  headers: string[],
  rows: string[][],
  citedUuids: string[]
): string[] | undefined {
  const companyIndex = headers.findIndex(isCompanyDescriptor);
  if (companyIndex < 0) return undefined;

  const citedRows = rows.filter((row) =>
    citedUuids.some((id) => id && row.some((cell) => cell.includes(id)))
  );
  const sourceRows = citedRows.length > 0 ? citedRows : rows;
  const entities = collectCompanyEntities(sourceRows.map((row) => row[companyIndex] ?? ""));
  return entities.length > 0 ? entities : undefined;
}

function collectCompanyEntities(cells: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const cell of cells) {
    const company = getCompanyNameFromCell(cell);
    if (!company || seen.has(company)) continue;
    seen.add(company);
    out.push(company);
  }
  return out;
}

function isCompanyDescriptor(value: string): boolean {
  const normalized = stripInlineMarkup(value).toLowerCase();
  return (
    normalized === "name" ||
    normalized.includes("company") ||
    normalized.includes("issuer")
  );
}

function getCompanyNameFromCell(value: string): string | undefined {
  const text = stripInlineMarkup(value);
  if (!text || isGenericEntityLabel(text)) return undefined;

  const commaParts = text.split(",").map((part) => part.trim()).filter(Boolean);
  if (commaParts.length > 1 && isLikelyTicker(commaParts[0])) {
    return commaParts.slice(1).join(", ");
  }

  const [firstToken, ...restTokens] = text.split(/\s+/);
  if (restTokens.length > 0 && isLikelyTicker(firstToken)) {
    return restTokens.join(" ");
  }

  if (isLikelyTicker(text)) return undefined;
  return text;
}

function isGenericEntityLabel(value: string): boolean {
  const normalized = value.toLowerCase().replace(/\s+/g, " ").trim();
  return (
    normalized === "ticker" ||
    normalized === "company" ||
    normalized === "company name" ||
    normalized === "ticker, company name" ||
    normalized === "ticker company name"
  );
}

function isLikelyTicker(value: string): boolean {
  return /^[A-Z0-9.]{1,8}(?:-[A-Z]{2})?$/.test(value.trim());
}
